import { gql } from '@apollo/client';
import type { ScanPromptInfo } from '../../pages/pda/scanResult';

/** 轻量查询：仅包牌基础信息，不拉订单列表，用于PDA装包扫描等高频场景 */
export const GET_BAG_BASIC_INFO_BY_NUMBER = gql`
  query GetBagBasicInfoByNumber($bagNumber: String!) {
    getBagBasicInfoByNumber(bagNumber: $bagNumber) {
      id
      bagNumber
      businessType
      fromLocation
      nextStop
      sortingCode
      status
      carrierCode
      printType
      warehouseId
      promptSortingCode
      ttBizType
      isPPMOD
      bagContainerId
      containerCandidates {
        id
        code
        name
      }
      success
      message
      promptInfo {
        messageKey
        popupType
        title
        content
      }
    }
  }
`;

/** PPMOD 容器候选（与后端 BagContainerCandidate 对齐） */
export interface BagContainerCandidateType {
  id: number;
  code: number;
  name: string;
  /** 操作类型：1-TT / 2-TEMU；前端按 bag.externalBusinessType 派生 */
  operateType: number;
}

/**
 * 页面级容器候选查询。
 *  - operateType=null：返回全部启用容器（TT + TEMU）。装包页首次进入用这个，一次性加载后本地过滤
 *  - operateType=1/2：仅返回该 operateType 的容器（PC 精确筛选场景）
 */
export const LIST_ACTIVE_BAG_CONTAINERS = gql`
  query ListActiveBagContainers($operateType: Int) {
    listActiveBagContainers(operateType: $operateType) {
      id
      code
      name
      operateType
    }
  }
`;

/** 与后端 BagContainerOperateType 对齐：TT=1 / TEMU=2 */
export enum BagContainerOperateType {
  TT = 1,
  TEMU = 2,
}

export interface ListActiveBagContainersResponse {
  listActiveBagContainers: BagContainerCandidateType[];
}

/** getBagBasicInfoByNumber 响应（字段打平，与后端 BagBasicInfoResponse 对齐） */
export interface BagBasicInfoType {
  success: boolean;
  message?: string | null;
  id?: string | null;
  bagNumber?: string | null;
  businessType?: number | null;
  fromLocation?: string | null;
  nextStop?: string | null;
  sortingCode?: string | null;
  status?: number | null;
  carrierCode?: number | null;
  /** 打印模板类型 1-SwiftX 2-TT */
  printType?: number | null;
  /** 创建站点ID */
  warehouseId?: number | null;
  /** 展示用分拣段码：SWX/TEMU 取 sortingCode，TT 取第一分拣规则（有预分拣码时拼接为一个字段，如 LAX1-LAX1-UNI-UNI_SEA01） */
  promptSortingCode?: string | null;
  /** TT分拣业务类型：FBT/CBT/UNLIMITED(不限)；null=存量包牌/非TT */
  ttBizType?: string | null;
  /** 是否 PPMOD 包牌（TEMU_SORT + USPS + SCF + LA 目的地）；true 时前端展示容器类型下拉 */
  isPPMOD?: boolean | null;
  /** 当前绑定的容器主键（bag_container.id）；未绑定为 null */
  bagContainerId?: number | null;
  /** PPMOD 容器候选列表；isPPMOD=false 时为空 */
  containerCandidates?: BagContainerCandidateType[] | null;
  promptInfo?: ScanPromptInfo | null;
}

/** 尾程商选项，与后端 CarrierOptionType 对应 */
export interface CarrierOptionType {
  value: number;
  name: string;
}

/** 获取尾程商选项列表（WarehouseQuery.getAllCarrierOptions） */
export const GET_ALL_CARRIER_OPTIONS = gql`
  query GetAllCarrierOptions {
    getAllCarrierOptions {
      value
      name
    }
  }
`;

export interface GetAllCarrierOptionsResponse {
  getAllCarrierOptions: CarrierOptionType[];
}

/** 0-未使用 1-已使用 2-已废弃 3-已使用(尾程交接) */
export const BAG_STATUS_DISCARDED = 2;
export const BAG_STATUS_HANDOVER = 3;

export interface GetBagBasicInfoByNumberResponse {
  getBagBasicInfoByNumber: BagBasicInfoType;
}

export interface GetBagBasicInfoByNumberVariables {
  bagNumber: string;
}

/**
 * PDA 新装包页更换容器：与 PC 包牌列表 UPDATE_BAG_CONTAINER 同一后端入口。
 * - 关包及之后状态禁改；换容器后 PPMOD 限重超限抛 PP_BAG_WEIGHT_LIMIT_EXCEEDED
 * - 返回结构对齐后端 UpdateBagContainerResult：success/errorMessage/errorCode/bag
 * - bag 内只取本页要用的字段，避免拉取整段 BagTagFields
 */
export const UPDATE_BAG_CONTAINER = gql`
  mutation UpdateBagContainer($bagId: Long!, $bagContainerId: Long) {
    updateBagContainer(bagId: $bagId, bagContainerId: $bagContainerId) {
      success
      errorMessage
      errorCode
      bag {
        id
        bagContainerId
        containerCode
        containerName
      }
    }
  }
`;

export interface UpdateBagContainerVariables {
  bagId: number;
  bagContainerId?: number | null;
}

export interface UpdateBagContainerResponse {
  updateBagContainer: {
    success: boolean;
    errorMessage?: string | null;
    errorCode?: string | null;
    bag?: {
      id: number;
      bagContainerId?: number | null;
      containerCode?: number | null;
      containerName?: string | null;
    } | null;
  };
}

/** 装包扫描候选包牌：与后端 BaggingScanCandidateBagInput 对应 */
export interface BaggingScanCandidateBagInput {
  bagNumber: string;
  sortingCode: string;
  carrierCode?: number | null;
  /** PPMOD 装包：前端选中的容器主键（bag_container.id），非 PP 场景传 null，后端归一化时会丢弃 */
  bagContainerId?: number | null;
}

/** 装包扫描：后端匹配段码，直接返回 BaggingScanResponse（无外壳包裹） */
export const HANDLE_WHS_BAGGING_SCAN_EVENT = gql`
  mutation HandleWhsBaggingScanEvent($trackingNumber: String!, $candidateBags: [BaggingScanCandidateBagInput!]!, $clientReportKey: String) {
    result: handleWhsBaggingScanEvent(trackingNumber: $trackingNumber, candidateBags: $candidateBags, clientReportKey: $clientReportKey) {
      success
      message
      matchedBagNumber
      promptInfo {
        messageKey
        popupType
        title
        content
      }
    }
  }
`;

/** 旧版前端 gql（仅传 bagNumber，灰度兼容用）；PPMOD 非必填的容器 id 附带传入 */
export const HANDLE_WHS_BAGGING_SCAN_EVENT_LEGACY = gql`
  mutation HandleWhsBaggingScanEvent(
    $bagNumber: String!,
    $trackingNumber: String!,
    $clientReportKey: String,
    $bagContainerId: Long
  ) {
    result: handleWhsBaggingScanEvent(
      bagNumber: $bagNumber,
      trackingNumber: $trackingNumber,
      clientReportKey: $clientReportKey,
      bagContainerId: $bagContainerId
    ) {
      success
      message
      matchedBagNumber
      promptInfo {
        messageKey
        popupType
        title
        content
      }
    }
  }
`;

export interface HandleWhsBaggingScanEventVariables {
  trackingNumber: string;
  candidateBags: BaggingScanCandidateBagInput[];
  clientReportKey?: string;
}

export interface HandleWhsBaggingScanEventLegacyVariables {
  bagNumber: string;
  trackingNumber: string;
  clientReportKey?: string;
  /** PPMOD 单包路径：前端选中的容器主键（bag_container.id），可空（非必填） */
  bagContainerId?: number | null;
}

// 与后端 BaggingScanResponse 对齐：mutation 直接返回 BaggingScanResponse，无外壳包裹
export interface BaggingScanResponse {
  success: boolean;
  matchedBagNumber?: string | null;
  promptInfo?: ScanPromptInfo | null;
  message?: string | null;
}

export interface HandleWhsBaggingScanEventResponse {
  result: BaggingScanResponse;
}
